import { type KeyboardLayout } from '../../onboarding/keyboard/useKeyboardLayout';

/**
 * A "legend" is the set of glyphs printed on the physical keys. Only three
 * distinct legends are perceptible on the small, decorative onboarding keyboard:
 * QWERTY, AZERTY, and QWERTZ. The eight regions the detector reports collapse
 * onto these three.
 */
export type LegendId = 'qwerty' | 'azerty' | 'qwertz';

/** What a single physical key prints (and the characters it emits). */
export type LegendOverride = { label: string; eventKeys: string[] };

/**
 * Overrides keyed by `KeyboardEvent.code` (the *physical* key position, e.g.
 * `KeyQ`). Anchoring overrides to `code` — never to the printed label — is what
 * keeps hotkey matching correct: a hotkey stored as `['KeyQ']` resolves to the
 * same physical key regardless of which letter the legend paints on it.
 */
export type Legend = Partial<Record<string, LegendOverride>>;

/**
 * AZERTY (France / Belgium). The letters A/Z/Q/W/M move and the bottom
 * punctuation row shifts one position. We repaint only what's legible at this
 * scale — the letters and the four bottom-row glyphs — and leave the digit row
 * as plain numbers (the shifted symbols would just add noise).
 */
const AZERTY: Legend = {
  KeyQ: { label: 'A', eventKeys: ['a', 'A'] },
  KeyW: { label: 'Z', eventKeys: ['z', 'Z'] },
  KeyA: { label: 'Q', eventKeys: ['q', 'Q'] },
  KeyZ: { label: 'W', eventKeys: ['w', 'W'] },
  Semicolon: { label: 'M', eventKeys: ['m', 'M'] },
  KeyM: { label: ',', eventKeys: [','] },
  Comma: { label: ';', eventKeys: [';'] },
  Period: { label: ':', eventKeys: [':'] },
  Slash: { label: '!', eventKeys: ['!'] },
};

/**
 * QWERTZ (Germany / Austria / Switzerland). Only Y and Z swap. The umlauts
 * (Ü/Ö/Ä) are deliberately omitted: they differ between the German and Swiss
 * base layers and both regions map here, so the Y↔Z swap is the only override
 * correct for both.
 */
const QWERTZ: Legend = {
  KeyY: { label: 'Z', eventKeys: ['z', 'Z'] },
  KeyZ: { label: 'Y', eventKeys: ['y', 'Y'] },
};

export const LEGENDS: Record<LegendId, Legend> = {
  qwerty: {},
  azerty: AZERTY,
  qwertz: QWERTZ,
};

/** Maps a detected region to its printed-letter legend. */
export const regionToLegend = (region: KeyboardLayout): LegendId => {
  switch (region) {
    case 'france-belgium':
      return 'azerty';
    case 'germany-austria':
    case 'switzerland':
      return 'qwertz';
    default:
      return 'qwerty';
  }
};
